import graphql__d25aeaa79c0116edc1bf from "./__generated__/InvoiceList_query.graphql.ts";import * as React from "react"
import {useEffect, useTransition} from "react"
import {graphql} from "react-relay";
import {usePaginationFragment} from "react-relay/hooks";
import LoadMoreButton from "./presentational/LoadMoreButton";
import ActionButton from "./presentational/button/ActionButton";
import {faFileMagnifyingGlass, faPencil, faTrashCan} from "@fortawesome/pro-regular-svg-icons";
import DeleteInvoiceDialog from "./dialog/DeleteInvoiceDialog";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import TableHeader from "./presentational/table/TableHeader";
import Table from "./presentational/table/Table";
import TableData, {TextAlignment} from "./presentational/table/TableData";
import {resolvePath, useNavigate} from "react-router-dom";
import Dinero from "dinero.js";
import {FormattedDate, useIntl} from "react-intl";
import StatusBadge from "./presentational/StatusBadge";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleExclamation} from "@fortawesome/pro-solid-svg-icons";
import PaymentMethodIcon from "./presentational/PaymentMethodIcon";

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

export type FilterOption = 'active' | 'overdue' | 'done' | 'all';

type Props = {
    queryRef: any // FIXME: Any type we can define here? It's actually: Readonly<{ ' $data'?: unknown; ' $fragmentRefs': unknown; }> | null
    searchTerm?: string
    filter: FilterOption
    onLoadingChange?: (isLoading: boolean) => void
}

type InvoiceListProps = ConnectedProps<typeof connector> & Props;

const InvoiceList: React.FC<InvoiceListProps> = (props) => {
    const intl = useIntl();
    const {dispatchShowModal, dispatchHideModal, filter} = props
    const navigate = useNavigate();
    const [isPending, startTransition] = useTransition();

    const getInvoiceStateLabel = (state: string): string => {
        switch (state) {
            case 'DRAFT':
                return intl.formatMessage({id: 'list.invoice.state.draft'});
            case 'INVOICE_NUMBER_PENDING':
                return intl.formatMessage({id: 'list.invoice.state.invoiceNumberPending'});
            case 'OPEN':
                return intl.formatMessage({id: 'list.invoice.state.open'});
            case 'PAID':
                return intl.formatMessage({id: 'list.invoice.state.paid'});
            case 'REFUNDED':
                return intl.formatMessage({id: 'list.invoice.state.refunded'});
            case 'REVERSED':
                return intl.formatMessage({id: 'list.invoice.state.reversed'});
            case 'UNCOLLECTIBLE':
                return intl.formatMessage({id: 'list.invoice.state.uncollectible'});
            case 'VOID':
                return intl.formatMessage({id: 'list.invoice.state.void'});
            default:
                return state;
        }
    };

    const {data, hasNext, loadNext, isLoadingNext, refetch} = usePaginationFragment(
        graphql__d25aeaa79c0116edc1bf,
        props.queryRef
    );

    useEffect(() => {
        // Use startTransition to keep the old data visible while loading new data
        // This prevents the Suspense fallback from showing
        const graphqlFilter = filter.toUpperCase(); // Map 'active' -> 'ACTIVE', etc.
        startTransition(() => {
            refetch({searchTerm: props.searchTerm, filter: graphqlFilter}, {fetchPolicy: 'store-and-network'});
        });
    }, [props.searchTerm, filter, refetch])

    useEffect(() => {
        // Notify parent component about loading state
        props.onLoadingChange?.(isPending);
    }, [isPending, props]);

    if (!data.invoices || data.invoices.totalCount === 0) {
        return (<></>);
    }

    return (
        <div className="flex-1 min-h-0 relative overflow-y-auto" aria-label={intl.formatMessage({id: 'navigation.invoices'})}>
            <Table>
                <thead>
                    <tr>
                        <TableHeader className="hidden sm:table-cell min-w-24">{intl.formatMessage({id: 'list.invoice.header.state'})}</TableHeader>
                        <TableHeader className="hidden sm:table-cell">{intl.formatMessage({id: 'list.invoice.header.invoiceDate'})}</TableHeader>
                        <TableHeader className="hidden sm:table-cell">{intl.formatMessage({id: 'list.invoice.header.dueDate'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.invoice.header.invoiceNumber'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.invoice.header.customer'})}</TableHeader>
                        <TableHeader textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'list.invoice.header.total'})}</TableHeader>
                        <TableHeader className="hidden sm:table-cell"/>
                        <TableHeader className="hidden sm:table-cell"/>
                    </tr>
                </thead>
                <tbody>
                    {data.invoices.edges.map(
                        (edge, index) => {
                            if (!edge.node) { // why can this happen?
                                return null;
                            }
                            const invoice = edge.node;
                            const {customerAccount} = invoice;
                            const {customerName} = customerAccount;
                            const showDeleteInvoiceDialog = () => {
                                dispatchShowModal({
                                    body: (<DeleteInvoiceDialog handleCloseDialog={dispatchHideModal} queryRef={invoice}/>)
                                })
                            };

                            const totalGrossAmount = Dinero({amount: invoice.totalGrossAmount});

                            // Calculate days overdue (only count full days)
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const dueDate = new Date(invoice.dueDate);
                            dueDate.setHours(0, 0, 0, 0);
                            const daysDifference = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
                            const daysOverdue = invoice.state === 'OPEN' && daysDifference > 0 ? daysDifference : 0;

                            // when WE process the payment (card and SEPA debit), any status except 'succeeded' or 'pending' hint at a failure
                            let isPaymentFailing = false;
                            if (invoice.paymentIntents.length > 0 && (invoice.paymentMethod.paymentMethodType == 'Card' || invoice.paymentMethod.paymentMethodType == 'SepaDebit')) {
                                isPaymentFailing = true;
                                for (const paymentIntent of invoice.paymentIntents) {
                                    if (paymentIntent.status === 'SUCCEEDED' || paymentIntent.status === 'PENDING') {
                                        isPaymentFailing = false;
                                        break;
                                    }
                                }
                            }

                            return (
                                <tr key={index} className={(index % 2 === 0 ? 'bg-white hover:bg-gray-100' : 'bg-gray-50 hover:bg-gray-100') + " group"}>
                                    <TableData className={'hidden sm:table-cell pl-4' + (isPaymentFailing ? ' border-red-600 border-l-4 ' : '')}>
                                        <StatusBadge
                                            value={invoice.state}
                                            label={getInvoiceStateLabel(invoice.state)}
                                            states={{
                                                green: ['PAID', 'REFUNDED'],
                                                yellow: ['OPEN', 'VOID', 'REVERSED'],
                                                red: ['UNCOLLECTIBLE'],
                                                gray: ['DRAFT', 'INVOICE_NUMBER_PENDING']
                                            }}
                                        />
                                    </TableData>
                                    <TableData className="hidden sm:table-cell"><FormattedDate value={edge.node.invoiceDate} day="2-digit" month="2-digit" year="numeric"/></TableData>
                                    <TableData className={"hidden sm:table-cell" + (daysOverdue >= 30 ? ' text-red-600' : daysOverdue >= 14 ? ' text-orange-500' : '')}>
                                        <FormattedDate value={edge.node.dueDate} day="2-digit" month="2-digit" year="numeric"/>
                                        {daysOverdue > 0 && <span className="ml-1">(+{daysOverdue})</span>}
                                    </TableData>
                                    <TableData>{invoice.state === 'DRAFT' ? '-' : invoice.invoiceNumber  }</TableData>
                                    <TableData>{customerName}</TableData>
                                    <TableData textAlignment={TextAlignment.Right}>{totalGrossAmount.toFormat()}</TableData>
                                    <TableData textAlignment={TextAlignment.Center} className="hidden sm:table-cell w-14">
                                        {invoice.paymentMethod && (<PaymentMethodIcon paymentMethodType={invoice.paymentMethod.paymentMethodType} cardBrand={invoice.paymentMethod.cardBrand}/>)}
                                    </TableData>
                                    <TableData textAlignment={TextAlignment.Right}>
                                        {invoice.state === 'DRAFT' &&
                                            <>
                                                <ActionButton additionalClassname="invisible group-hover:visible" icon={faTrashCan} title={intl.formatMessage({id: 'list.invoice.action.delete'})} onClick={() => {
                                                    showDeleteInvoiceDialog()
                                                }}/>
                                                <ActionButton additionalClassname="invisible group-hover:visible" icon={faPencil} title={intl.formatMessage({id: 'list.invoice.action.edit'})} onClick={(event) => {
                                                    let to = "/invoices/" + invoice.id;
                                                    if (invoice.state === 'DRAFT') {
                                                        to += "/edit";
                                                    }
                                                    if (event.shiftKey || event.metaKey) {
                                                        event.preventDefault()
                                                        window.open(resolvePath(to).pathname)
                                                    } else {
                                                        navigate(to)
                                                    }
                                                }}/>
                                            </>
                                        }
                                        {invoice.state !== 'DRAFT' &&
                                            <ActionButton additionalClassname="invisible group-hover:visible" icon={faFileMagnifyingGlass} title={intl.formatMessage({id: 'list.invoice.action.show'})} onClick={(event) => {
                                                let to = "/invoices/" + invoice.id;
                                                if (event.shiftKey || event.metaKey) {
                                                    event.preventDefault()
                                                    window.open(resolvePath(to).pathname)
                                                } else {
                                                    navigate(to)
                                                }
                                            }}/>
                                        }
                                    </TableData>
                                </tr>
                            )
                        }
                    )}
                </tbody>
            </Table>
            <div className={'mt-8 flex gap-4'}>
                <div><strong>{intl.formatMessage({id: 'list.invoice.legend.title'})}</strong></div>
                <div className={'pl-2 border-red-600 border-l-4'}>{intl.formatMessage({id: 'list.invoice.legend.paymentIsFailing'})}</div>
                <div className={'text-orange-500'}>{intl.formatMessage({id: 'list.invoice.legend.paymentIsOverdue14'})}</div>
                <div className={'text-red-600'}>{intl.formatMessage({id: 'list.invoice.legend.paymentIsOverdue30'})}</div>
            </div>
            {hasNext && <LoadMoreButton onClick={() => loadNext(100)} isLoadingNext={isLoadingNext}/>}
        </div>
    )
}

export default connector(InvoiceList);
